import { Button, PageHeading, Spinner } from "@nu-assist/ui";
import { useQuery } from "@tanstack/react-query";
import { ChevronDown, ChevronUp, ChevronsUpDown, RefreshCw, X } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { fetchActivity } from "./api";
import type { ActivityEvent, ActivityPage } from "./types";

// ---------------------------------------------------------------------------
// Event label + badge
// ---------------------------------------------------------------------------

function formatEventLabel(event: string, details: Record<string, unknown>): string {
  if (event === "drift.alert") {
    const status = (details?.status as string | undefined) ?? "";
    if (status === "needs_onboarding") return "pending onboard";
    if (status === "offboarding_pending") return "offboard pending";
    if (status === "leftover_seat") return "leftover seat";
    if (status === "alias_mismatch") return "alias mismatch";
    return status.replace(/_/g, " ") || "alert";
  }
  if (event === "drift.run.completed") return "drift check";
  const parts = event.split(".");
  const action = parts.slice(1).join(".").replace(/_/g, " ");
  return action || event;
}

function eventColor(event: string): string {
  if (event === "drift.alert") return "bg-orange-500/15 dark:bg-orange-400/20 text-orange-700 dark:text-orange-400";
  if (event === "drift.run.completed") return "bg-surface text-text-secondary";
  if (event.includes("onboard")) return "bg-green-500/15 dark:bg-green-400/20 text-green-700 dark:text-green-400";
  if (event.includes("offboard")) return "bg-red-500/15 dark:bg-red-400/20 text-red-700 dark:text-red-400";
  if (event.includes("add_admin")) return "bg-purple-500/15 dark:bg-purple-400/20 text-purple-700 dark:text-purple-400";
  if (event.includes("remove_admin")) return "bg-red-500/15 dark:bg-red-400/20 text-red-700 dark:text-red-400";
  if (event.includes("change_email")) return "bg-blue-500/15 dark:bg-blue-400/20 text-blue-700 dark:text-blue-400";
  if (event.includes("set_on_leave")) return "bg-yellow-500/15 dark:bg-yellow-400/20 text-yellow-700 dark:text-yellow-400";
  if (event.includes("check")) return "bg-surface text-text-secondary";
  return "bg-surface text-text-secondary";
}

function resultColor(result: string): string {
  if (result === "ok" || result === "success") return "text-green-600 dark:text-green-400";
  if (result === "flagged" || result === "error" || result === "failed") return "text-red-600 dark:text-red-400";
  return "text-text-secondary";
}

function EventBadge({ event, details }: { event: string; details: Record<string, unknown> }) {
  return (
    <span
      className={[
        "inline-block px-2 py-0.5 rounded-full text-label font-medium",
        eventColor(event),
      ].join(" ")}
    >
      {formatEventLabel(event, details)}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Filter chip
// ---------------------------------------------------------------------------

function FilterChip({ label, onRemove }: { label: string; onRemove: () => void }) {
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-label border border-accent bg-accent/10 text-accent">
      {label}
      <button
        type="button"
        aria-label={`Remove ${label} filter`}
        onClick={onRemove}
        className="hover:opacity-70 transition-opacity"
      >
        <X className="w-3 h-3" />
      </button>
    </span>
  );
}

// ---------------------------------------------------------------------------
// Sortable column header
// ---------------------------------------------------------------------------

type SortCol = "ts" | "service" | "event" | "subject_email";
type SortDir = "asc" | "desc";

function SortIcon({ col, sortCol, sortDir }: { col: SortCol; sortCol: SortCol; sortDir: SortDir }) {
  if (col !== sortCol) return <ChevronsUpDown className="w-3 h-3 opacity-40" />;
  return sortDir === "desc"
    ? <ChevronDown className="w-3 h-3" />
    : <ChevronUp className="w-3 h-3" />;
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

const APP_OPTIONS = [
  { value: "", label: "All services" },
  { value: "nu-assist", label: "nu-assist" },
  { value: "nu-sync", label: "nu-sync" },
  { value: "drift", label: "drift" },
];

const DATE_RANGE_OPTIONS = [
  { value: "today", label: "Today" },
  { value: "7", label: "Last 7 days" },
  { value: "15", label: "Last 15 days" },
  { value: "30", label: "Last 30 days" },
];

function dateFromForRange(range: string): string {
  if (range === "today") {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d.toISOString();
  }
  const d = new Date();
  d.setDate(d.getDate() - Number(range));
  return d.toISOString();
}

const SORTABLE_COLS: { key: SortCol; label: string }[] = [
  { key: "ts", label: "When" },
  { key: "service", label: "Service" },
  { key: "event", label: "Event" },
  { key: "subject_email", label: "Subject" },
];
const EXTRA_COLS = ["Actor", "Result"];

export function AdminActivityPage() {
  const [beforeTs, setBeforeTs] = useState<string | undefined>(undefined);
  const [allItems, setAllItems] = useState<ActivityEvent[]>([]);

  // Filter state
  const [personFilter, setPersonFilter] = useState("");
  const [personInput, setPersonInput] = useState("");
  const [appFilter, setAppFilter] = useState("");
  const [dateRange, setDateRange] = useState("today");

  // Sort state (in-memory, applied after accumulation)
  const [sortCol, setSortCol] = useState<SortCol>("ts");
  const [sortDir, setSortDir] = useState<SortDir>("desc");

  const dateFromFilter = dateFromForRange(dateRange);

  const { data, isLoading, error, refetch, isFetching } = useQuery<ActivityPage>({
    queryKey: ["admin-activity", beforeTs, personFilter, appFilter, dateRange],
    queryFn: () =>
      fetchActivity({
        beforeTs: beforeTs,
        dateFrom: dateFromFilter,
        person: personFilter || undefined,
        app: appFilter || undefined,
      }),
    staleTime: 0,
    refetchOnWindowFocus: true,
    retry: false,
  });

  useEffect(() => {
    if (!data) return;
    if (beforeTs === undefined) {
      setAllItems(data.items);
    } else {
      setAllItems((prev) => [...prev, ...data.items]);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  function handleRefresh() {
    setBeforeTs(undefined);
    setAllItems([]);
    refetch();
  }

  function handleLoadMore() {
    if (data?.next_cursor) {
      setBeforeTs(data.next_cursor);
    }
  }

  function handleFilterChange() {
    setBeforeTs(undefined);
    setAllItems([]);
  }

  function handleSort(col: SortCol) {
    if (sortCol === col) {
      setSortDir((d) => (d === "desc" ? "asc" : "desc"));
    } else {
      setSortCol(col);
      setSortDir("desc");
    }
  }

  // Base items: use accumulated list when paginating, otherwise current page.
  const baseItems = beforeTs !== undefined ? allItems : (data?.items ?? []);

  const sortedItems = useMemo(() => {
    if (sortCol === "ts" && sortDir === "desc") return baseItems;
    return [...baseItems].sort((a, b) => {
      let cmp = 0;
      switch (sortCol) {
        case "ts":
          cmp = new Date(a.ts).getTime() - new Date(b.ts).getTime();
          break;
        case "service":
          cmp = (a.service ?? "").localeCompare(b.service ?? "");
          break;
        case "event":
          cmp = formatEventLabel(a.event, a.details).localeCompare(
            formatEventLabel(b.event, b.details),
          );
          break;
        case "subject_email":
          cmp = (a.subject_email ?? "").localeCompare(b.subject_email ?? "");
          break;
      }
      return sortDir === "desc" ? -cmp : cmp;
    });
  }, [baseItems, sortCol, sortDir]);

  return (
    <div className="min-h-full px-6 py-6 flex flex-col gap-5">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <PageHeading
          title="Activity log"
          description="Audit trail of every admin action and drift check, sourced from CloudWatch Logs. Results are scoped to the selected date range — use the Person filter to search a specific user across all dates."
        />
        <div className="shrink-0 mt-1">
          <Button variant="secondary" onClick={handleRefresh} disabled={isFetching}>
            <RefreshCw
              className={`w-3.5 h-3.5 ${isFetching ? "animate-spin" : ""}`}
              aria-hidden="true"
            />
            {isFetching ? "Refreshing…" : "Refresh"}
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-end gap-3">
        {/* Person filter */}
        <div className="flex flex-col gap-1">
          <label className="text-label text-text-secondary">Person</label>
          <div className="flex gap-1">
            <input
              type="text"
              placeholder="email or name…"
              className="border border-border rounded px-2 py-1 text-body bg-bg text-text placeholder:text-text-muted focus:outline-none focus:ring-1 focus:ring-accent w-44"
              value={personInput}
              onChange={(e) => setPersonInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && personInput.trim()) {
                  setPersonFilter(personInput.trim());
                  handleFilterChange();
                }
              }}
            />
            <Button
              variant="secondary"
              onClick={() => {
                if (personInput.trim()) {
                  setPersonFilter(personInput.trim());
                  handleFilterChange();
                }
              }}
            >
              Filter
            </Button>
          </div>
        </div>

        {/* App filter */}
        <div className="flex flex-col gap-1">
          <label className="text-label text-text-secondary">Service</label>
          <select
            className="border border-border rounded px-2 py-1.5 text-body bg-bg text-text focus:outline-none focus:ring-1 focus:ring-accent"
            value={appFilter}
            onChange={(e) => {
              setAppFilter(e.target.value);
              handleFilterChange();
            }}
          >
            {APP_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>

        {/* Date range filter */}
        <div className="flex flex-col gap-1">
          <label className="text-label text-text-secondary">Date range</label>
          <select
            className="border border-border rounded px-2 py-1.5 text-body bg-bg text-text focus:outline-none focus:ring-1 focus:ring-accent"
            value={dateRange}
            onChange={(e) => {
              setDateRange(e.target.value);
              handleFilterChange();
            }}
          >
            {DATE_RANGE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Active filter chips */}
      {personFilter && (
        <div className="flex flex-wrap gap-2">
          <FilterChip
            label={`Person: ${personFilter}`}
            onRemove={() => {
              setPersonFilter("");
              setPersonInput("");
              handleFilterChange();
            }}
          />
        </div>
      )}

      {/* Loading */}
      {isLoading && (
        <div className="flex items-center gap-2 py-8">
          <Spinner size="sm" />
          <span className="text-body text-text-secondary">Loading activity log…</span>
        </div>
      )}

      {/* Error */}
      {error && !isLoading && (
        <p className="text-body text-error py-4">
          Failed to load activity log.{" "}
          <button type="button" className="underline" onClick={handleRefresh}>
            Retry
          </button>
        </p>
      )}

      {/* Table */}
      {!isLoading && !error && (
        <>
          <div className="overflow-x-auto rounded-md border border-border bg-table-surface">
            <table className="w-full border-collapse">
              <thead>
                <tr className="border-b border-border text-left">
                  {SORTABLE_COLS.map(({ key, label }) => (
                    <th
                      key={key}
                      scope="col"
                      className="text-label font-semibold text-text-secondary px-3 py-2 cursor-pointer select-none hover:text-text transition-colors"
                      onClick={() => handleSort(key)}
                    >
                      <span className="inline-flex items-center gap-1">
                        {label}
                        <SortIcon col={key} sortCol={sortCol} sortDir={sortDir} />
                      </span>
                    </th>
                  ))}
                  {EXTRA_COLS.map((col) => (
                    <th
                      key={col}
                      scope="col"
                      className="text-label font-semibold text-text-secondary px-3 py-2"
                    >
                      {col}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {sortedItems.map((entry, idx) => (
                  <tr
                    key={`${entry.operation_id}-${entry.event}-${idx}`}
                    className="border-b border-border hover:bg-surface-hover transition-colors last:border-0"
                  >
                    <td className="py-2.5 px-3 text-body text-text-secondary whitespace-nowrap">
                      {new Date(entry.ts).toLocaleString()}
                    </td>
                    <td className="py-2.5 px-3 text-body text-text-secondary">
                      {entry.service}
                    </td>
                    <td className="py-2.5 px-3">
                      <EventBadge event={entry.event} details={entry.details} />
                    </td>
                    <td className="py-2.5 px-3 text-body text-text-secondary">
                      {entry.subject_email ?? "—"}
                    </td>
                    <td className="py-2.5 px-3 text-body text-text-secondary">
                      {entry.actor_email ?? "—"}
                    </td>
                    <td className={`py-2.5 px-3 text-body whitespace-nowrap ${resultColor(entry.result)}`}>
                      {entry.result}
                    </td>
                  </tr>
                ))}
                {sortedItems.length === 0 && (
                  <tr>
                    <td
                      colSpan={SORTABLE_COLS.length + EXTRA_COLS.length}
                      className="py-10 text-center text-body text-text-secondary"
                    >
                      No activity yet.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {data?.next_cursor && (
            <div className="flex justify-center pt-2">
              <Button variant="secondary" onClick={handleLoadMore} disabled={isFetching}>
                {isFetching ? "Loading…" : "Load more"}
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
